import { Loader2 } from "lucide-react";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import VacancyInformation from "./VacancyInformation";

import Icons from "@/components/icons";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import Tabs from "@/components/ui/v2/tabs";
import { VacancyHistoryTab } from "@/features/vacancies/components/history/VacancyHistoryTab";
import { cn } from "@/lib/utils/cn";
import { isSafeExternalUrl, openExternalUrl } from "@/lib/utils/externalUrl";
import { getVacancyStatus } from "@/lib/vacancies";
import { VacancyType } from "@/types/vacancy/VacancyType";

const DRAWER_CONSTANTS = {
  LABELS: {
    DESCRIPTIONS: "Descriptions",
    LINK_TO_VACANCY: "Link to vacancy",
  },
  ICON_SIZE: 22,
} as const;

const publicationAgeStyles = (category: string): string => {
  if (category === "Newer than 45 days") {
    return "bg-success/10 text-success";
  }
  if (category === "Older than 45 days") {
    return "bg-warning/10 text-warning";
  }
  return "bg-destructive/10 text-destructive";
};

type VacancyDetailsDrawerProps = {
  clientId?: string;
  isFetching?: boolean;
  isLoading?: boolean;
  onNotFound?: () => void;
  vacancy?: VacancyType;
};

const VacancyDetailsDrawer: React.FC<VacancyDetailsDrawerProps> = ({
  clientId,
  isFetching = false,
  isLoading = false,
  onNotFound,
  vacancy,
}) => {
  const { t } = useTranslation();
  const vacancyOnline = useCallback(
    (actual: string | null, expireDate: string | null) => {
      const status = getVacancyStatus(actual, expireDate);
      if (status === "unpublished") {
        return {
          status,
          variant: "neutral" as const,
        };
      }
      if (status === "expired") {
        return { status, variant: "archive" as const };
      }
      return { status, variant: "success" as const };
    },
    []
  );
  useEffect(() => {
    if (!vacancy && !isLoading && !isFetching) {
      onNotFound?.();
    }
  }, [vacancy, isLoading, isFetching, onNotFound]);
  const formatVacancyDate = useCallback((dateString: string | null): string => {
    if (!dateString) return "";

    try {
      const [date] = new Date(dateString)
        .toLocaleString("nl-NL", { dateStyle: "short" })
        .split(", ");
      return (
        date
          ?.split("/")
          .map((part) => (part.length === 1 ? `0${part}` : part))
          .join("-") || ""
      );
    } catch {
      return "";
    }
  }, []);

  if (isLoading || isFetching) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <Loader2 className="size-16 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!vacancy) {
    return null;
  }

  const { title, actual, publID, jobSourceUrl } = vacancy;

  const formattedDate = formatVacancyDate(actual);

  const handleExternalLinkClick = () => {
    openExternalUrl(vacancy?.link);
  };

  const onlineStatus = vacancyOnline(vacancy.actual, vacancy.expires);

  return (
    <div className="flex h-full flex-col bg-card">
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 sm:gap-8 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <h2 className="wrap-break-word">{title}</h2>
          <Button
            variant="outline"
            size="sm"
            className="w-full shrink-0 sm:w-auto"
            onClick={handleExternalLinkClick}
            disabled={!isSafeExternalUrl(vacancy.link)}
          >
            <Icons.LinkIcon fontSize={DRAWER_CONSTANTS.ICON_SIZE} />
            {DRAWER_CONSTANTS.LABELS.LINK_TO_VACANCY}
          </Button>
        </div>
        <div className="flex w-full flex-wrap items-center gap-x-6 gap-y-2">
          <Badge variant={onlineStatus.variant}>
            <span className="size-2 rounded-full bg-current" />
            {t(`common.${onlineStatus.status}`) || "unknown"}
          </Badge>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Icons.CalendarIcon fontSize={16} />
            <span className="text-sm">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Icons.TagIcon fontSize={16} />
            {publID && isSafeExternalUrl(jobSourceUrl) ? (
              <a
                href={jobSourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {publID}
              </a>
            ) : (
              <span className="text-sm">{publID}</span>
            )}
          </div>
          {vacancy.publicationAgeCategory && (
            <span
              className={cn(
                "ml-auto rounded-lg px-2 py-0.5 text-sm font-medium",
                publicationAgeStyles(vacancy.publicationAgeCategory)
              )}
            >
              {vacancy.publicationAgeCategory}
            </span>
          )}
        </div>
        <Tabs
          defaultTabId="details"
          tabs={[
            {
              id: "details",
              label: t("admin.vacancies.drawer.detailsTab"),
              content: (
                <VacancyInformation vacancy={vacancy} clientId={clientId} />
              ),
            },
            {
              id: "history",
              label: t("admin.vacancies.drawer.historyTab"),
              content: <VacancyHistoryTab vacancyId={vacancy.id} />,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default VacancyDetailsDrawer;
